import IconCatch, { findIcon } from '@/components/icons/icon-catch/icon-catch';
import useIsMobile from '@/hooks/use-is-mobile';
import { formatRiskRound } from '@/utils';
import { Badge, Box, ButtonIcon, Divider, Flex, IconArrowRight, IconPlus, Image, Link, Text } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';

type RiskFactorsBreakdownCardProps = {
  id: string;
  iconId: string;
  title: string;
  subtitle?: string;
  isExpanded: boolean;
  toggleExpanded: () => void;
  onOpenRiskFactorModal?: () => void;
  headerPills?: {
    subtitle: string;
    color: string;
    value: string;
    textColor: string;
    testId?: string;
  }[];
  hideChildren?: boolean;
  link?: string;
  average?: 'above' | 'below' | 'average';
  risk?: number | string;
  hideRiskBadge?: boolean;
};

const aboveBelowProps = {
  above: {
    styles: {
      bgColor: 'alert-error-default',
    },
    text: 'Above Average Risk',
  },
  below: {
    styles: {
      bgColor: 'alert-success-default',
    },
    text: 'Below Average Risk',
  },
  average: {
    styles: {
      bgColor: 'alert-warning-default',
    },
    text: 'Average Risk',
  },
};

export const RiskFactorsBreakdownCard = ({
  id,
  iconId,
  title,
  subtitle,
  isExpanded,
  toggleExpanded,
  headerPills = [],
  children,
  hideChildren = false,
  link,
  onOpenRiskFactorModal,
  average = 'average',
  risk = 0,
  hideRiskBadge = false,
}: RiskFactorsBreakdownCardProps & React.PropsWithChildren) => {
  const isMobile = useIsMobile();

  const Header = () => {
    return (
      <Box data-anchor-id={id} onClick={toggleExpanded}>
        <Flex direction="row" justifyContent="space-between">
          <Flex direction="row" gap="spacer-4" alignItems="center">
            <IconCatch backgroundColor="tan" icon={findIcon(iconId) ?? 'biomarkers'} alt={title} />
            {link && (
              <Link
                as={ReactRouterLink}
                to={link}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                trailingIcon={<IconArrowRight />}
                _hover={{ textDecoration: 'none' }}
                textDecoration={'underline'}
              >
                <Text variant="text-header-M" textTransform="capitalize">
                  {title}
                </Text>
              </Link>
            )}
            {onOpenRiskFactorModal && (
              <Text
                variant="text-header-M"
                textTransform="capitalize"
                textDecoration={'underline'}
                _hover={{ textDecoration: 'none', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenRiskFactorModal();
                }}
              >
                {title}
                <IconArrowRight margin={2} />
              </Text>
            )}
          </Flex>

          <Flex direction="row" gap="spacer-4" alignItems="center">
            {!isExpanded && !hideRiskBadge && (
              <Badge
                color="primary"
                borderRadius="radii-2"
                textTransform="none"
                px="4"
                py="1"
                fontWeight="normal"
                {...aboveBelowProps[average].styles}
              >
                {isMobile ? formatRiskRound(risk) : aboveBelowProps[average].text}
              </Badge>
            )}
            <ButtonIcon
              data-testid={`risk-factors-breakdown-card-expand-toggle-${id}`}
              aria-label="Expand"
              backgroundColor="transparent"
              _hover={{ backgroundColor: 'transparent' }}
              _active={{ backgroundColor: 'transparent' }}
              color="dark-blue"
              icon={isExpanded ? <Image src="/images/icons/minus.svg" /> : <IconPlus />}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded();
              }}
            />
          </Flex>
        </Flex>
      </Box>
    );
  };

  const Pills = () => {
    const pills = headerPills.slice(0, 3);

    return (
      <Flex direction="row" gap="spacer-4" width="100%">
        {pills.map(({ subtitle, color, value, textColor, testId }) => (
          <Flex direction="column" gap="spacer-2" flex="1" key={subtitle}>
            <Box
              height={{ base: '35px', md: '45px' }}
              backgroundColor={color}
              borderRadius="radii-2"
              alignItems="center"
              justifyContent="center"
              display="flex"
              p={2}
            >
              <Text
                variant={{ base: 'text-body-medium', md: 'text-body-display-XS' }}
                color={textColor}
                // HACK: Sometimes the value is a long string like '1.4 servings / week',
                // in which case we need to shrink the font size so it doesn't look too crowded.
                fontSize={{
                  md: value.length >= 30 ? '1rem' : '1.25rem',
                  base: value.length >= 15 ? '0.8rem' : '1rem',
                }}
                lineHeight={'1.25rem'}
                data-testid={testId}
              >
                {value}
              </Text>
            </Box>
            <Text variant="text-body-floating-label-regular">{subtitle}</Text>
          </Flex>
        ))}
      </Flex>
    );
  };

  return (
    <Box
      backgroundColor="white"
      padding="spacer-4"
      borderRadius="radii-3"
      data-testid={`risk-factors-breakdown-card-${id}_${isExpanded ? 'expanded' : 'collapsed'}`}
      display="flex"
      flexDirection="column"
      gap="spacer-4"
    >
      <Header />
      {isExpanded && (
        <>
          {subtitle && (
            <Text variant="text-body-meta-regular" pl="70px" pr="15px" mb={1}>
              {subtitle}
            </Text>
          )}
          <Pills />
          {!hideChildren && (
            <>
              <Divider borderColor="ui-element-divider" opacity={1} />
              <Box>{children}</Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};
